import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";

type Choice = { id: string; label: string };
type LinkHit = { href: string; title: string; space: string };
type Turn = {
  role: "user" | "assistant";
  content: string;
  wire?: string;
  choices?: Choice[];
  links?: LinkHit[];
};

type Props = { lang: Lang; name: string; onChanged: () => void };

export function AgentChat({ lang, name, onChanged }: Props) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function send(next: Turn[]) {
    setBusy(true);
    setError("");
    setTurns(next);
    try {
      const res = await hieraApi.agent(next.map((turn) => ({ role: turn.role, content: turn.wire || turn.content })));
      setTurns([
        ...next,
        { role: "assistant", content: res.reply, choices: res.choices, links: res.links },
      ]);
      if (res.changed) onChanged();
    } catch (err) {
      const code = (err as Error).message;
      setError(t(lang, code === "agent_off" ? "agentOff" : "agentFail"));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const content = text.trim();
    if (!content || busy) return;
    setText("");
    void send([...turns, { role: "user", content }]);
  }

  function pick(choice: Choice) {
    if (busy) return;
    void send([
      ...turns,
      { role: "user", content: choice.label, wire: `Выбор: ${choice.label} (${choice.id})` },
    ]);
  }

  return (
    <section className="agent">
      <h1>
        {t(lang, "goodMorning")}, {name}
      </h1>
      <div className="agent-log">
        {turns.map((turn, i) => (
          <div key={i} className={`agent-msg ${turn.role}`}>
            <p>{turn.content}</p>
            {turn.links?.length ? (
              <ul>
                {turn.links.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href}>{link.title || "Untitled"}</Link>
                    {link.space ? <span>{link.space}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {turn.choices?.length && i === turns.length - 1 ? (
              <div className="agent-choices">
                {turn.choices.map((choice) => (
                  <button key={choice.id} type="button" disabled={busy} onClick={() => pick(choice)}>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {error ? <p className="muted">{error}</p> : null}
      <form className="agent-form" onSubmit={onSubmit}>
        <input
          value={text}
          disabled={busy}
          placeholder={t(lang, "whatNext")}
          aria-label={t(lang, "whatNext")}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="agent-send" disabled={busy || !text.trim()}>
          {t(lang, "agentSend")}
        </button>
      </form>
    </section>
  );
}
